import React, { useState, useEffect, useMemo } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { authFetch, getLocalApiUrl } from '../../config/api'
import toast from 'react-hot-toast'
import {
  UserCircleIcon,
  PencilIcon,
  KeyIcon,
  ShieldCheckIcon,
  DevicePhoneMobileIcon,
  EnvelopeIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline'
import MfaTotpCodeHint from '../../components/MfaTotpCodeHint'

const PROFILE_SECTIONS = [
  { id: 'profile', label: 'Profile' },
  { id: 'preferences', label: 'Preferences' },
  { id: 'notifications', label: 'Notifications' },
  { id: 'account', label: 'Account' },
  { id: 'kyc', label: 'KYC' },
  { id: 'security', label: 'Security' },
  { id: 'permissions', label: 'Permissions' },
]

function formatPermissionLabel(permission) {
  return String(permission).replace(/_/g, ' ').replace(':', ' → ')
}

function groupPermissions(permissions) {
  const groups = new Map()
  for (const perm of permissions) {
    const raw = String(perm || '').trim()
    if (!raw) continue
    const slash = raw.indexOf('/')
    const colon = raw.indexOf(':')
    let group = 'general'
    if (slash > 0) group = raw.slice(0, slash)
    else if (colon > 0) group = raw.slice(0, colon)
    else group = raw
    if (!groups.has(group)) groups.set(group, [])
    groups.get(group).push(raw)
  }
  return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b))
}

function SectionCard({ id, title, description, children }) {
  return (
    <section id={id} className="scroll-mt-24 bg-white shadow rounded-lg p-6">
      <div className="mb-6">
        <h2 className="text-lg font-medium text-gray-900">{title}</h2>
        {description ? <p className="mt-1 text-sm text-gray-500">{description}</p> : null}
      </div>
      {children}
    </section>
  )
}

function MfaEnrollPanel({ mfaEnroll, mfaCode, setMfaCode, mfaBusy, onCancel, onConfirm, onNewQr }) {
  const [secondsLeft, setSecondsLeft] = useState(null)

  useEffect(() => {
    if (!mfaEnroll?.expiresAt) {
      setSecondsLeft(null)
      return undefined
    }
    const tick = () => {
      const left = Math.max(0, Math.floor((new Date(mfaEnroll.expiresAt).getTime() - Date.now()) / 1000))
      setSecondsLeft(left)
    }
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [mfaEnroll?.expiresAt])

  const expired = secondsLeft === 0

  return (
    <div className="border rounded-lg p-4 space-y-3 bg-gray-50 max-w-md">
      <p className="text-sm text-gray-700">
        Scan with your authenticator app, then enter the current 6-digit code.
      </p>
      {secondsLeft != null && (
        <p className={`text-xs ${expired ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
          {expired
            ? 'Setup session expired — generate a new QR code and scan again.'
            : `QR setup valid for ${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, '0')}`}
        </p>
      )}
      {mfaEnroll.qrDataUrl && !expired && (
        <img src={mfaEnroll.qrDataUrl} alt="MFA QR code" className="mx-auto w-44 h-44 border bg-white rounded" />
      )}
      <input
        type="text"
        inputMode="numeric"
        maxLength={6}
        className="input-field text-center tracking-widest"
        placeholder="000000"
        value={mfaCode}
        disabled={expired}
        onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
      />
      {!expired && <MfaTotpCodeHint />}
      <div className="flex flex-col gap-2">
        <div className="flex gap-2">
          <button type="button" className="btn-secondary flex-1" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary flex-1"
            disabled={mfaBusy || expired || mfaCode.length !== 6}
            onClick={onConfirm}
          >
            Confirm
          </button>
        </div>
        <button
          type="button"
          className="text-sm text-[#0A5169] hover:underline disabled:opacity-50"
          disabled={mfaBusy}
          onClick={onNewQr}
        >
          Generate new QR code
        </button>
      </div>
    </div>
  )
}

function ProfileNav({ activeId, onJump }) {
  return (
    <nav aria-label="Profile sections" className="space-y-1">
      {PROFILE_SECTIONS.map((section) => {
        const active = activeId === section.id
        return (
          <button
            key={section.id}
            type="button"
            onClick={() => onJump(section.id)}
            className={`w-full text-left px-3 py-2 rounded-md text-sm font-medium transition-colors ${
              active
                ? 'bg-[#0A5169] text-white'
                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            }`}
          >
            {section.label}
          </button>
        )
      })}
    </nav>
  )
}

function Profile() {
  const { user: authUser, permissions: permissionSet } = useAuth()
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [isEditing, setIsEditing] = useState(false)
  const [editForm, setEditForm] = useState({})
  const [showChangePassword, setShowChangePassword] = useState(false)
  const [saving, setSaving] = useState(false)
  const [pw, setPw] = useState({ current: '', next: '', confirm: '' })
  const [mfaStatus, setMfaStatus] = useState({ enabled: false, enabledAt: null, loading: true })
  const [mfaEnroll, setMfaEnroll] = useState(null)
  const [mfaCode, setMfaCode] = useState('')
  const [mfaBusy, setMfaBusy] = useState(false)
  const [showDisableMfa, setShowDisableMfa] = useState(false)
  const [disableMfa, setDisableMfa] = useState({ password: '', code: '' })
  const [activeSection, setActiveSection] = useState('profile')
  const [permissionsExpanded, setPermissionsExpanded] = useState(false)
  const [openPermissionGroups, setOpenPermissionGroups] = useState(() => new Set())

  const permissionList = useMemo(() => {
    if (permissionSet && typeof permissionSet[Symbol.iterator] === 'function') {
      return [...permissionSet].sort()
    }
    if (Array.isArray(authUser?.permissions)) return [...authUser.permissions].sort()
    return []
  }, [permissionSet, authUser?.permissions])

  const permissionGroups = useMemo(() => groupPermissions(permissionList), [permissionList])

  const jumpToSection = (id) => {
    setActiveSection(id)
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)
        if (visible[0]?.target?.id) setActiveSection(visible[0].target.id)
      },
      { rootMargin: '-20% 0px -60% 0px', threshold: [0, 0.25, 0.5, 1] }
    )
    PROFILE_SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(id)
      if (el) observer.observe(el)
    })
    return () => observer.disconnect()
  }, [user])

  useEffect(() => {
    if (authUser?.username) {
      loadUserData()
      loadMfaStatus()
    }
  }, [authUser])

  const loadMfaStatus = async () => {
    try {
      setMfaStatus((s) => ({ ...s, loading: true }))
      const res = await authFetch(getLocalApiUrl('/api/account/mfa/status'))
      const j = await res.json()
      if (j.success) {
        setMfaStatus({ enabled: !!j.enabled, enabledAt: j.enabledAt || null, loading: false })
      } else {
        setMfaStatus((s) => ({ ...s, loading: false }))
      }
    } catch (_) {
      setMfaStatus((s) => ({ ...s, loading: false }))
    }
  }

  const startMfaEnroll = async ({ forceNew = false } = {}) => {
    setMfaBusy(true)
    try {
      const res = await authFetch(getLocalApiUrl('/api/account/mfa/enroll/start'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ forceNew }),
      })
      const j = await res.json()
      if (!j.success) throw new Error(j.error || 'Failed to start enrolment')
      setMfaEnroll({
        qrDataUrl: j.qrDataUrl,
        otpauthUrl: j.otpauthUrl,
        issuer: j.issuer,
        expiresAt: j.expiresAt || null,
      })
      setMfaCode('')
      if (forceNew) toast.success('New QR code generated — rescan in your authenticator app')
    } catch (e) {
      toast.error(e.message || 'Could not start MFA enrolment')
    } finally {
      setMfaBusy(false)
    }
  }

  const confirmMfaEnroll = async () => {
    setMfaBusy(true)
    try {
      const res = await authFetch(getLocalApiUrl('/api/account/mfa/enroll/confirm'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: mfaCode }),
      })
      const j = await res.json()
      if (!j.success) throw new Error(j.error || 'Invalid code')
      toast.success('Two-factor authentication is now enabled')
      setMfaEnroll(null)
      setMfaCode('')
      await loadMfaStatus()
    } catch (e) {
      toast.error(e.message || 'Verification failed')
    } finally {
      setMfaBusy(false)
    }
  }

  const submitDisableMfa = async () => {
    setMfaBusy(true)
    try {
      const res = await authFetch(getLocalApiUrl('/api/account/mfa/disable'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(disableMfa),
      })
      const j = await res.json()
      if (!j.success) throw new Error(j.error || 'Could not disable MFA')
      toast.success('Two-factor authentication disabled')
      setShowDisableMfa(false)
      setDisableMfa({ password: '', code: '' })
      await loadMfaStatus()
    } catch (e) {
      toast.error(e.message || 'Could not disable MFA')
    } finally {
      setMfaBusy(false)
    }
  }

  const loadUserData = async () => {
    try {
      setLoading(true)
      const [usersRes, refRes] = await Promise.all([
        authFetch(getLocalApiUrl('/api/admin/users')),
        authFetch(getLocalApiUrl('/api/admin/reference-data')),
      ])
      const usersResult = await usersRes.json()
      const refResult = await refRes.json()

      const rawRef = refResult.success && refResult.data && typeof refResult.data === 'object' ? refResult.data : {}
      const refData = {
        teams: Array.isArray(rawRef.teams) ? rawRef.teams : [],
        departments: Array.isArray(rawRef.departments) ? rawRef.departments : [],
        locations: Array.isArray(rawRef.locations) ? rawRef.locations : [],
        titles: rawRef.titles && typeof rawRef.titles === 'object' ? rawRef.titles : {},
      }

      if (usersResult.success) {
        const dbUser = (usersResult.data || []).find((u) => u.username === authUser.username)
        if (dbUser) {
          const nameParts = (dbUser.name || authUser.name || '').split(' ')
          const firstName = nameParts[0] || authUser.username
          const lastName = nameParts.slice(1).join(' ') || ''
          const roleKey = authUser.role || dbUser.role || 'viewer'

          const enrichedUser = {
            id: authUser.username,
            username: authUser.username,
            email: dbUser.email || `${authUser.username}@company.com`,
            firstName,
            lastName,
            role: roleKey.toUpperCase(),
            team: dbUser.team || (refData.teams[0] || ''),
            phone: dbUser.phone || '',
            department: dbUser.department || (refData.departments[0] || ''),
            title: dbUser.title || refData.titles?.[roleKey] || 'User',
            location: dbUser.location || (refData.locations[0] || ''),
            timezone: 'Australia/Sydney',
            language: 'English (AU)',
            dateFormat: 'DD/MM/YYYY',
            lastKycDate: '2025-06-15T10:00:00Z',
            lastKycDocument: 'Driver Licence',
            faceprintConfidence: 92,
            membershipDate: dbUser.created_at || '2023-02-10T00:00:00Z',
            lastLogin: null,
            createdAt: dbUser.created_at || new Date().toISOString(),
            emailNotifications: true,
            smsNotifications: false,
          }

          setUser(enrichedUser)
          setEditForm({
            firstName: enrichedUser.firstName,
            lastName: enrichedUser.lastName,
            phone: enrichedUser.phone,
            department: enrichedUser.department,
            title: enrichedUser.title,
            location: enrichedUser.location,
            timezone: enrichedUser.timezone,
            language: enrichedUser.language,
            dateFormat: enrichedUser.dateFormat,
          })
        }
      }
    } catch (err) {
      console.error('Error loading user data:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleSaveProfile = async () => {
    setSaving(true)
    try {
      const fullName = `${editForm.firstName} ${editForm.lastName}`.trim()
      const response = await authFetch(getLocalApiUrl(`/api/admin/users/${encodeURIComponent(authUser.username)}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: fullName,
          email: user.email,
          phone: editForm.phone,
        }),
      })

      const result = await response.json()
      if (!result.success) throw new Error(result.error || 'Failed to update profile')

      setUser({ ...user, ...editForm, name: fullName })
      setIsEditing(false)
      toast.success('Profile updated')
    } catch (err) {
      toast.error(err.message || 'Failed to update profile')
    } finally {
      setSaving(false)
    }
  }

  const handleNotificationChange = (type, value) => {
    setUser({ ...user, [type]: value })
  }

  const togglePermissionGroup = (group) => {
    setOpenPermissionGroups((prev) => {
      const next = new Set(prev)
      if (next.has(group)) next.delete(group)
      else next.add(group)
      return next
    })
  }

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Never'
    return new Date(timestamp).toLocaleString('en-AU', {
      timeZone: 'Australia/Sydney',
      dateStyle: 'medium',
      timeStyle: 'short',
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    )
  }

  if (!user) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <h3 className="text-yellow-800 font-medium mb-2">User Not Found</h3>
        <p className="text-yellow-600 text-sm">Could not load your profile information.</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center shrink-0">
            <UserCircleIcon className="w-10 h-10 text-gray-500" />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-2xl font-bold text-gray-900">
              {user.firstName} {user.lastName}
            </h1>
            <p className="text-gray-600">{user.title} · {user.role}</p>
            <p className="text-sm text-gray-500 truncate">{user.email}</p>
          </div>
        </div>
        <p className="mt-4 text-sm text-gray-600">
          Manage your account settings, preferences, and security options.
        </p>
      </div>

      <div className="lg:hidden overflow-x-auto pb-1">
        <div className="flex gap-2 min-w-max">
          {PROFILE_SECTIONS.map((section) => (
            <button
              key={section.id}
              type="button"
              onClick={() => jumpToSection(section.id)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap ${
                activeSection === section.id
                  ? 'bg-[#0A5169] text-white'
                  : 'bg-white text-gray-600 border border-gray-200'
              }`}
            >
              {section.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex gap-8 items-start">
        <aside className="hidden lg:block w-52 shrink-0 sticky top-6">
          <div className="bg-white shadow rounded-lg p-3">
            <ProfileNav activeId={activeSection} onJump={jumpToSection} />
          </div>
        </aside>

        <div className="flex-1 min-w-0 space-y-6">
          <SectionCard id="profile" title="Basic information" description="Your name and contact details.">
            <div className="flex justify-end -mt-2 mb-4">
              <button type="button" onClick={() => setIsEditing(!isEditing)} className="btn-secondary">
                <PencilIcon className="w-4 h-4 mr-2" />
                {isEditing ? 'Cancel' : 'Edit'}
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                ['First Name', 'firstName', 'text'],
                ['Last Name', 'lastName', 'text'],
                ['Phone Number', 'phone', 'tel'],
                ['Department', 'department', 'text'],
                ['Job Title', 'title', 'text'],
                ['Location', 'location', 'text'],
              ].map(([label, key, type]) => (
                <div key={key}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
                  {isEditing ? (
                    <input
                      type={type}
                      value={editForm[key]}
                      onChange={(e) => setEditForm({ ...editForm, [key]: e.target.value })}
                      className="input-field"
                    />
                  ) : (
                    <p className="text-sm text-gray-900">{user[key] || '—'}</p>
                  )}
                </div>
              ))}
            </div>
            {isEditing && (
              <div className="mt-6 flex justify-end gap-3">
                <button type="button" onClick={() => setIsEditing(false)} className="btn-secondary">
                  Cancel
                </button>
                <button type="button" onClick={handleSaveProfile} disabled={saving} className="btn-primary">
                  {saving ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            )}
          </SectionCard>

          <SectionCard id="preferences" title="Preferences" description="Regional and display settings for your account.">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Timezone</label>
                <select
                  value={user.timezone}
                  onChange={(e) => setUser({ ...user, timezone: e.target.value })}
                  className="select-field"
                >
                  <option value="Australia/Sydney">Australia/Sydney</option>
                  <option value="Australia/Melbourne">Australia/Melbourne</option>
                  <option value="Australia/Brisbane">Australia/Brisbane</option>
                  <option value="Australia/Perth">Australia/Perth</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Language</label>
                <select
                  value={user.language}
                  onChange={(e) => setUser({ ...user, language: e.target.value })}
                  className="select-field"
                >
                  <option value="English (AU)">English (AU)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Date format</label>
                <select
                  value={user.dateFormat}
                  onChange={(e) => setUser({ ...user, dateFormat: e.target.value })}
                  className="select-field"
                >
                  <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                  <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                </select>
              </div>
            </div>
          </SectionCard>

          <SectionCard id="notifications" title="Notifications" description="How you receive alerts from PaySafeGO.">
            <div className="space-y-4">
              <div className="flex items-center justify-between py-2">
                <div className="flex items-center gap-3">
                  <EnvelopeIcon className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Email notifications</p>
                    <p className="text-sm text-gray-500">Alerts and updates via email</p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={user.emailNotifications}
                    onChange={(e) => handleNotificationChange('emailNotifications', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-[#0A5169] peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all" />
                </label>
              </div>
              <div className="flex items-center justify-between py-2 border-t border-gray-100">
                <div className="flex items-center gap-3">
                  <DevicePhoneMobileIcon className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">SMS notifications</p>
                    <p className="text-sm text-gray-500">Critical alerts via SMS</p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={user.smsNotifications}
                    onChange={(e) => handleNotificationChange('smsNotifications', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-[#0A5169] peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all" />
                </label>
              </div>
            </div>
          </SectionCard>

          <SectionCard id="account" title="Account" description="Role and membership details (read-only).">
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 text-sm">
              <div>
                <dt className="text-gray-500">Username</dt>
                <dd className="font-medium text-gray-900">{user.username}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Role</dt>
                <dd className="font-medium text-gray-900">{user.role}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Team</dt>
                <dd className="font-medium text-gray-900">{user.team?.replace(/_/g, ' ') || '—'}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Member since</dt>
                <dd className="font-medium text-gray-900">{formatTimestamp(user.createdAt)}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Last login</dt>
                <dd className="font-medium text-gray-900">{formatTimestamp(user.lastLogin)}</dd>
              </div>
            </dl>
          </SectionCard>

          <SectionCard id="kyc" title="KYC snapshot" description="Identity verification summary for your staff profile.">
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 text-sm">
              <div>
                <dt className="text-gray-500">Last KYC date</dt>
                <dd className="font-medium text-gray-900">{formatTimestamp(user.lastKycDate)}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Last document</dt>
                <dd className="font-medium text-gray-900">{user.lastKycDocument}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Faceprint confidence</dt>
                <dd className="font-medium text-gray-900">{user.faceprintConfidence}%</dd>
              </div>
            </dl>
            <p className="mt-4 pt-4 border-t text-xs text-gray-600">
              {(() => {
                const monthsSince = Math.max(
                  0,
                  Math.floor((Date.now() - new Date(user.lastKycDate).getTime()) / (1000 * 60 * 60 * 24 * 30))
                )
                return monthsSince >= 12
                  ? 'Re-KYC recommended (last KYC older than 12 months).'
                  : 'KYC is recent.'
              })()}
            </p>
          </SectionCard>

          <SectionCard id="security" title="Security" description="Password and two-factor authentication.">
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 rounded-lg border border-gray-200 bg-gray-50">
                <div className="flex items-start gap-3">
                  <ShieldCheckIcon className={`w-5 h-5 mt-0.5 shrink-0 ${mfaStatus.enabled ? 'text-green-500' : 'text-gray-400'}`} />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Two-factor authentication</p>
                    <p className="text-xs text-gray-500">Google or Microsoft Authenticator (optional)</p>
                  </div>
                </div>
                {mfaStatus.loading ? (
                  <span className="text-sm text-gray-500">Checking…</span>
                ) : (
                  <span className={`text-sm font-medium ${mfaStatus.enabled ? 'text-green-600' : 'text-gray-500'}`}>
                    {mfaStatus.enabled ? 'Enabled' : 'Off'}
                  </span>
                )}
              </div>

              {!mfaStatus.enabled && !mfaEnroll && (
                <button
                  type="button"
                  onClick={() => startMfaEnroll()}
                  disabled={mfaBusy || mfaStatus.loading}
                  className="btn-secondary"
                >
                  <DevicePhoneMobileIcon className="w-4 h-4 mr-2 inline" />
                  Enable two-factor authentication
                </button>
              )}

              {mfaEnroll && (
                <MfaEnrollPanel
                  mfaEnroll={mfaEnroll}
                  mfaCode={mfaCode}
                  setMfaCode={setMfaCode}
                  mfaBusy={mfaBusy}
                  onCancel={() => { setMfaEnroll(null); setMfaCode('') }}
                  onConfirm={confirmMfaEnroll}
                  onNewQr={() => startMfaEnroll({ forceNew: true })}
                />
              )}

              {mfaStatus.enabled && (
                <div className="pt-2 border-t border-gray-100">
                  <p className="text-xs font-medium text-gray-500 mb-3">Account actions</p>
                  <div className="flex flex-col gap-3 sm:max-w-md">
                    <button
                      type="button"
                      onClick={() => setShowDisableMfa(true)}
                      className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-red-200 bg-white text-red-700 text-sm font-medium hover:bg-red-50 transition-colors w-full sm:w-auto"
                    >
                      Disable two-factor authentication
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowChangePassword(true)}
                      className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-gray-200 bg-white text-gray-800 text-sm font-medium hover:bg-gray-50 transition-colors w-full sm:w-auto"
                    >
                      <KeyIcon className="w-4 h-4 shrink-0" />
                      Change password
                    </button>
                  </div>
                </div>
              )}

              {!mfaStatus.enabled && (
                <div className="pt-2 border-t border-gray-100">
                  <p className="text-xs font-medium text-gray-500 mb-3">Account actions</p>
                  <button
                    type="button"
                    onClick={() => setShowChangePassword(true)}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-gray-200 bg-white text-gray-800 text-sm font-medium hover:bg-gray-50 transition-colors w-full sm:w-auto"
                  >
                    <KeyIcon className="w-4 h-4 shrink-0" />
                    Change password
                  </button>
                </div>
              )}
            </div>
          </SectionCard>

          <SectionCard
            id="permissions"
            title="Permissions"
            description="Capabilities assigned to your role in this organisation."
          >
            {permissionList.length === 0 ? (
              <p className="text-sm text-gray-500 italic">No permissions assigned.</p>
            ) : (
              <div className="space-y-4">
                <button
                  type="button"
                  onClick={() => setPermissionsExpanded((v) => !v)}
                  className="flex items-center justify-between w-full p-4 rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 text-left"
                >
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {permissionList.length} permission{permissionList.length === 1 ? '' : 's'} across {permissionGroups.length} area{permissionGroups.length === 1 ? '' : 's'}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {permissionsExpanded ? 'Click to collapse' : 'Click to view grouped list'}
                    </p>
                  </div>
                  {permissionsExpanded ? (
                    <ChevronDownIcon className="w-5 h-5 text-gray-500 shrink-0" />
                  ) : (
                    <ChevronRightIcon className="w-5 h-5 text-gray-500 shrink-0" />
                  )}
                </button>

                {permissionsExpanded && (
                  <div className="space-y-2 max-h-[28rem] overflow-y-auto pr-1">
                    {permissionGroups.map(([group, perms]) => {
                      const open = openPermissionGroups.has(group)
                      return (
                        <div key={group} className="border border-gray-200 rounded-lg overflow-hidden">
                          <button
                            type="button"
                            onClick={() => togglePermissionGroup(group)}
                            className="w-full flex items-center justify-between px-4 py-3 bg-white hover:bg-gray-50 text-left"
                          >
                            <span className="text-sm font-medium text-gray-900 capitalize">
                              {group.replace(/-/g, ' ')}
                              <span className="ml-2 text-gray-500 font-normal">({perms.length})</span>
                            </span>
                            {open ? (
                              <ChevronDownIcon className="w-4 h-4 text-gray-400" />
                            ) : (
                              <ChevronRightIcon className="w-4 h-4 text-gray-400" />
                            )}
                          </button>
                          {open && (
                            <ul className="px-4 pb-3 space-y-1 border-t border-gray-100 bg-gray-50/50">
                              {perms.map((permission) => (
                                <li key={permission} className="flex items-start gap-2 text-sm text-gray-700 py-1">
                                  <CheckCircleIcon className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                                  <span>{formatPermissionLabel(permission)}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )}
          </SectionCard>
        </div>
      </div>

      {showDisableMfa && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Disable two-factor authentication</h3>
            <p className="text-sm text-gray-600 mb-4">Enter your password and a current authenticator code.</p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                <input type="password" className="input-field" value={disableMfa.password} onChange={(e) => setDisableMfa((p) => ({ ...p, password: e.target.value }))} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Authenticator code</label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  className="input-field"
                  value={disableMfa.code}
                  onChange={(e) => setDisableMfa((p) => ({ ...p, code: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                />
                <MfaTotpCodeHint className="mt-2" />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button type="button" className="btn-secondary" onClick={() => { setShowDisableMfa(false); setDisableMfa({ password: '', code: '' }) }}>
                Cancel
              </button>
              <button type="button" className="btn-primary" disabled={mfaBusy} onClick={submitDisableMfa}>
                Disable MFA
              </button>
            </div>
          </div>
        </div>
      )}

      {showChangePassword && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Change password</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Current password</label>
                <input type="password" className="input-field" value={pw.current} onChange={(e) => setPw((p) => ({ ...p, current: e.target.value }))} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">New password</label>
                <input type="password" className="input-field" value={pw.next} onChange={(e) => setPw((p) => ({ ...p, next: e.target.value }))} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Confirm new password</label>
                <input type="password" className="input-field" value={pw.confirm} onChange={(e) => setPw((p) => ({ ...p, confirm: e.target.value }))} />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button type="button" onClick={() => setShowChangePassword(false)} className="btn-secondary">
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={async () => {
                  try {
                    if (!pw.next || pw.next !== pw.confirm) {
                      toast.error('Passwords do not match')
                      return
                    }
                    const r = await authFetch(getLocalApiUrl('/api/account/change-password'), {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ current_password: pw.current, new_password: pw.next }),
                    })
                    const j = await r.json()
                    if (!r.ok || !j.success) throw new Error(j.error || 'Failed')
                    toast.success('Password changed')
                    setShowChangePassword(false)
                    setPw({ current: '', next: '', confirm: '' })
                  } catch (e) {
                    toast.error(e.message || 'Failed to change password')
                  }
                }}
              >
                Update password
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Profile
